import type { PhotoData } from '../types';

const MAX_DIMENSION = 1280;

export async function normalizePhoto(source: Blob): Promise<Blob> {
  const image = await loadImage(source);
  if (!image.naturalWidth || !image.naturalHeight) throw new Error('This photo has invalid dimensions.');
  const scale = Math.min(1, MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('The browser could not prepare this photo.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The browser could not prepare this photo.')), 'image/jpeg', 0.74);
  });
}

export async function blobToBase64(photo: PhotoData) {
  const bytes = new Uint8Array(photo instanceof Blob ? await photo.arrayBuffer() : photo);
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export function base64ToBlob(base64: string, mimeType = 'image/jpeg') {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

export function photoToBlob(photo: PhotoData, mimeType = 'image/jpeg') {
  return photo instanceof Blob ? photo : new Blob([photo], { type: mimeType });
}

export async function photoForStorage(photo: PhotoData): Promise<ArrayBuffer> {
  return photo instanceof Blob ? photo.arrayBuffer() : photo;
}

function loadImage(blob: Blob) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('This photo format could not be read.'));
    };
    image.src = url;
  });
}
